import { STATE } from "@core/state";
import { DIRECTION_DOWN } from "@scroll/constants";
import Breakpoint from "@utils/breakpoint";
import { $, $$, body } from "@utils/dom";
import { on, off } from "@utils/listener";
import Viewport from "@utils/viewport";

export const CLASSNAME = "--js-site-search-opened";
const BREAKPOINT = ['(min-width: 1200px)'];

class SiteSearch {
  constructor(el, emitter) {
    this.el = el;
    this.emitter = emitter;
    this.triggers = [...$$(`[aria-controls="${this.el.id}"]`)];
    this.bg = $('.site-search__bg', this.el);
    this.container = $('.site-search__container', this.el);
    this.input = $('.search-form__input', this.el);

    this._bp = null;
    this._opened = false;

    this._close = this.close.bind(this);
    this._handleTriggers = this._handleTriggers.bind(this);
    this._handleKeyDown = this._handleKeyDown.bind(this);
    this._onOpenCompleted = this._onOpenCompleted.bind(this);
    this._onCloseCompleted = this._onCloseCompleted.bind(this);
    this._onScroll = this._onScroll.bind(this);
    this._onResize = this._onResize.bind(this);
  }

  init(){
    this._bp = Breakpoint(BREAKPOINT, this._onResize);
    this._bp.run();

    this._bindEvents();
  }
  destroy() {
    if( this._bp ) this._bp.dispose();

    this.el = null;
    this.emitter = null;
    this.triggers = null;
    this.bg = null;
    this.container = null;
    this.input = null;

    this._bp = null;
    this._opened = false;

    this._close = null;
    this._handleTriggers = null;
    this._handleKeyDown = null;
    this._onOpenCompleted = null;
    this._onCloseCompleted = null;
    this._onScroll = null;
    this._onResize = null;
  }

  stop() { this._unbindEvents(); }

  _bindEvents() {
    if( this._bp ) this._bp.on();
    if( this.triggers ) on(this.triggers, "click", this._handleTriggers);
    if( this.el ) on(window, "keydown", this._handleKeyDown);
    
    this.emitter.on("SiteNav.open", this._close);
    this.emitter.on("SiteSuperMenu.open", this._close);
    // this.emitter.on("SiteScroll.direction", this._onScroll);
  }
  _unbindEvents() {
    if( this._bp ) this._bp.off();
    if( this.triggers ) off(this.triggers, "click", this._handleTriggers);
    if( this.el ) off(window, "keydown", this._handleKeyDown);

    this.emitter.off("SiteNav.open", this._close);
    this.emitter.off("SiteSuperMenu.open", this._close);
    // this.emitter.off("SiteScroll.direction", this._onScroll);
  }

  open() {
    // if SiteSearch is already opened, do nothing
    if (this._opened === true) return;
    this._opened = true;

    // dispatch to state current status
    STATE.dispatch("SITE_SEARCH", this._opened);

    // unbind close completed callback
    if (this.bg) {
      off(this.bg, "transitionend", this._onCloseCompleted);
      off(this.bg, "transitionend", this._onOpenCompleted);
      on(this.bg, "transitionend", this._onOpenCompleted);
    }

    this.el.style.setProperty('--open-delay', `${STATE.siteSiteMenuOpen || STATE.siteNavOpen ? 450 : 0}ms`);

    // inform <body> & Application that SiteSearch is opened
    body.classList.add(CLASSNAME);
    this.emitter.emit("SiteSearch.open");

    this.emitter.emit("SiteScroll.stop", true);

    // inform each triggers that SiteSearch is opened
    this.triggers.forEach(btn => {
      btn.setAttribute("aria-expanded", true);
      btn.classList.add("is-active");
    });

    // set SiteSearch visible for screen readers
    this.el.setAttribute("aria-hidden", false);
  }
  close() {
    // if SiteSearch isn't opened, do nothing
    if (this._opened === false) return;
    this._opened = false;

    // dispatch to state current status
    STATE.dispatch("SITE_SEARCH", this._opened);

    // inform triggers that SiteSearch is closed
    this.triggers.forEach(btn => {
      btn.setAttribute("aria-expanded", false);
      btn.classList.remove("is-active");
    });

    // close callback
    this._onCloseCompleted();
    // if (this.bg) {
    //   off(this.bg, "transitionend", this._onOpenCompleted);
    //   off(this.bg, "transitionend", this._onCloseCompleted);
    //   on(this.bg, "transitionend", this._onCloseCompleted);
    // }

    // inform <body> & Application that SiteSearch is closed
    body.classList.remove(CLASSNAME);
    this.emitter.emit("SiteSearch.close");
  }
  toggle() {
    // regular SiteNav toggle handling
    if (this._opened === true) this.close();
    else this.open();
  }

  _handleTriggers(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.toggle();
  }
  _handleKeyDown(event) {
    if (this._opened === true && (event.key === "Escape" || event.key === "Esc")) {
      this.close();
    }
  }
  _onOpenCompleted() {
    if (this._opened === false) return;

    // focus on input
    this.input.focus();
  }
  _onCloseCompleted() {
    if (this._opened === true) return;

    this.emitter.emit("SiteScroll.start");

    // hide SiteSearch from screen readers
    this.el.setAttribute("aria-hidden", true);
  }
  _onScroll(direction) {
    if( !this._opened || direction !== DIRECTION_DOWN ) return;
    this.close();
  }
  _onResize() {
    if( Viewport.width < 1200 ) {
      this.close();
      this._onCloseCompleted();
    }
  }

  // getters
  get opened() {
    return this._opened;
  }
}

export default SiteSearch;
